import type { CustomerTeamMember } from '@clockwise/clients/customer-team';
import type { Customer } from '@clockwise/clients/customers';
import type { Product } from '@clockwise/clients/products';
import {
  isTimesheetEditable,
  type PatchTimesheetInput,
  type Timesheet,
} from '@clockwise/clients/timesheets';
import type { User } from '@clockwise/clients/users';
import {
  type EngagementResolution,
  type EngagementVersion,
  type TimesheetLockStage,
  timesheetLockStageFor,
} from '@clockwise/shared';
import { useQuery } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useInScopeProductsByCustomer } from '@/hooks/use-in-scope-products';
import { useRecentProductsByCustomer } from '@/hooks/use-recent-products';
import { useStableRoleLookup } from '@/hooks/use-stable-role-lookup';
import { productsApi } from '@/lib/api';
import { SHOW_OUT_OF_SCOPE } from '@/lib/out-of-scope';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { approvalResolutionText } from './approval-resolution-text';
import { ApprovalStatusBadge } from './approval-status-badge';
import { ConfirmDialog } from './confirm-dialog';
import { CustomerAutocomplete } from './customer-autocomplete';
import { OutOfScopeBadge } from './out-of-scope-badge';
import { ProductAutocomplete } from './product-autocomplete';
import { RecallEntryButton } from './recall-entry-button';
import { isActivityVisibleForCustomer } from './timesheet-activity-visibility';
import { BillableSwitch } from './timesheet-billable';
import { descriptionForActivityChange, isDescriptionPristine } from './timesheet-description';
import { TimesheetEngagementCell } from './timesheet-engagement-cell';
import { TimesheetEngagementDialog } from './timesheet-engagement-dialog';
import { useTimesheetMutations } from './timesheet-mutations';
import {
  engagementCandidatesForRow,
  engagementsActiveForRow,
  resolveRowEngagement,
} from './timesheet-row-engagement';
import {
  addDays,
  formatDelta,
  formatHours,
  hoursValidationError,
  isValidHoursInput,
  longDayName,
  STANDARD_HOURS_PER_DAY,
  shortDate,
  shortDayName,
  stickyBillable,
  sumHours,
  weekDates,
} from './timesheet-week';

/** Top row inside each card: customer | product | hours | description | trash. */
const CARD_GRID = 'grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.5fr)_80px_minmax(0,2fr)_auto] gap-3';

/**
 * The "Out of scope" badge on timesheet entries — off until out-of-scope
 * labelling is released. One switch for every surface: `SHOW_OUT_OF_SCOPE`.
 */
const SHOW_OUT_OF_SCOPE_BADGE = SHOW_OUT_OF_SCOPE;

interface Props {
  user: User;
  weekStart: string;
  day: string;
  entries: Timesheet[];
  loading: boolean;
  onPickDay: (date: string) => void;
  /** Customers the target user is on the team of in this week (ALL statuses),
   *  pre-fetched by the page route. Resolves the label + isInternal of a
   *  card's bound customer, including one deactivated after the entry was
   *  logged. NOT the dropdown's option list; see `selectableCustomers`. */
  teamCustomers: readonly Customer[];
  /** The subset of `teamCustomers` the per-card customer picker may OFFER —
   *  active only, filtered server-side by the page route. Keeps a deactivated
   *  customer out of the dropdown while `teamCustomers` still renders an
   *  already-bound one. */
  selectableCustomers: readonly Customer[];
  /** Per-day predicate driving the card's "this day is off the team" rule
   *  — backend enforces NOT_ON_TEAM (422) regardless. */
  isOnTeamForDay: (customerId: string, date: string) => boolean;
  /** All customer-team memberships for the target user that overlap the
   *  visible week. Drives the per-card product picker's role filter — see
   *  `roleIdsForEntry` for the lookup. */
  memberships: readonly CustomerTeamMember[];
  /** Every engagement (SOW) version covering the visible week for the target
   *  user's team customers. Same request the week grid uses — the page fetches
   *  it once and both views read the cache. */
  engagementVersions: readonly EngagementVersion[];
  /** `true` once that request has SETTLED. Until then a card must not be told
   *  "no engagement covers this" — an in-flight empty list is indistinguishable
   *  from a real zero. */
  engagementsLoaded: boolean;
  /** True when the given date falls in a closed timesheet period. Entries
   *  in a closed period render read-only — same treatment as
   *  submitted/approved — and the draft "add entry" card is suppressed.
   *  Backend is the backstop (a stale tab editing a closed date gets a
   *  409); this is the UI affordance.
   *
   *  `stage` selects which of the period's two boundaries to ask about, and
   *  defaults to the earlier `submission` one. See the identical prop on
   *  `TimesheetWeekGrid` for why it is declared rather than passed
   *  implicitly. */
  isDateLocked: (date: string, customerId: string | null, stage?: TimesheetLockStage) => boolean;
}

/** Imperative handle the page route drives for the ⌘/Ctrl+Enter shortcut.
 *  Day view has no "Add row" button — the blank draft card is always present —
 *  so "add a row" maps to focusing that card so the user can start typing. */
export interface TimesheetDayListHandle {
  /** Scroll the blank "add entry" card into view and focus its first control.
   *  No-op when the day is locked or off-team (no draft card rendered). */
  focusNewEntry: () => void;
  /** Scroll the card for `entryId` into view (e.g. a row just produced by
   *  committing a timer). Returns false when no card for that id is mounted —
   *  the caller retries after the next render once the refetch lands it. */
  scrollEntryIntoView: (entryId: string) => boolean;
}

/**
 * Single-day list of entry cards, mirroring the prototype: customer +
 * product + hours + description inline, BILLABLE pill below the row.
 *
 * Save model is "save on any value." A new entry is created the moment
 * the user types or picks anything; existing rows auto-save per field on
 * blur. Drafts can be incomplete (customer or hours NULL) — the backend
 * accepts that and only validates on submit. Required-but-missing fields
 * show a red dashed border so the user knows why a row isn't ready.
 */
export const TimesheetDayList = forwardRef<TimesheetDayListHandle, Props>(function TimesheetDayList(
  {
    user,
    weekStart,
    day,
    entries,
    loading,
    onPickDay,
    teamCustomers,
    selectableCustomers,
    isOnTeamForDay,
    memberships,
    engagementVersions,
    engagementsLoaded,
    isDateLocked,
  },
  ref,
) {
  // Per-card product picker filter: the assignee's per-customer role on
  // the entry's date (from customer_team_members.serviceRoleId), NOT
  // their global user.serviceRoleId. A user can serve different roles on
  // different customers; the catalog must reflect what they can actually
  // log against on THIS customer on THIS date.
  //
  // Fallback semantics when no membership covers (date) for (customerId):
  //   - existing entry → caller passes the entry's pinned `roleId`
  //     (historical role — the picker still scopes meaningfully for a
  //     grandfathered entry on an out-of-window date)
  //   - draft (no entry yet) → caller passes the assignee's global role
  //   - no customer yet → returns the module-level frozen EMPTY array
  //
  // The hook returns a stable lookup function whose identity survives
  // same-content refetches and whose returned arrays are referentially
  // stable per (customerId, date, fallbackRoleId) tuple — see
  // `useStableRoleLookup` for the cache + ref + biome-ignore mechanics.
  const roleIdsForEntry = useStableRoleLookup(memberships);
  // Single batched in-scope-products query for the displayed week. Shared
  // with the week-grid via TanStack's cache when both views use the same
  // (userId, fromDate, toDate) tuple, so toggling weekly ↔ day is a cache
  // hit. `isLoaded` lets us pass `undefined` to the picker while the data is
  // in flight (vs. `Set()` which would mean "fetched, empty").
  const weekEnd = addDays(weekStart, 6);
  const { byCustomer: inScopeByCustomer, isLoaded: inScopeLoaded } = useInScopeProductsByCustomer(
    user.id,
    weekStart,
    weekEnd,
  );
  // Recents for the picker's "used before by you" band — same cache tuple as
  // in-scope. No `isLoaded` gate: the band is a soft hint that fills in when
  // the query settles.
  const { byCustomer: recentByCustomer } = useRecentProductsByCustomer(user.id, weekStart, weekEnd);
  // Day-level off-team flag. When the target user isn't on ANY team for
  // this day in this week, we disable card creation for the day. Cell
  // editor still respects per-customer rules below.
  // Gate the draft "add entry" card on the SELECTABLE (active) set: if the
  // only customers the user is on the team of this day are inactive, the
  // picker would open empty, so there's nothing to add.
  const dayHasAnyTeam = selectableCustomers.some((c) => isOnTeamForDay(c.id, day));
  // Period lock for the displayed day. When the day is locked we render
  // existing entries read-only (via ExistingCard) and suppress the blank
  // draft card entirely — there's nothing new to add to a locked period.
  // Day-level check uses a null customer (no row context here): all-scope and
  // employee-scope unlocks re-open it; a customer-specific unlock doesn't
  // light up the new-draft card (the per-entry cards below evaluate scope
  // exactly, and the server accepts a save for that customer).
  const periodLocked = isDateLocked(day, null);
  const dates = weekDates(weekStart);
  const dayEntries = entries.filter((e) => e.date === day);

  // Resolve each displayed entry's activity to its own (`seed`) description so
  // the "description follows activity" rule can tell user-authored text from
  // text that merely echoes the prior activity. Batched by id — same query
  // shape (and TanStack cache) as the week-grid's product lookup.
  const productIds = useMemo(
    () =>
      Array.from(
        new Set(dayEntries.map((e) => e.productId).filter((v): v is string => v !== null)),
      ),
    [dayEntries],
  );
  const productsQuery = useQuery({
    queryKey: queryKeys.products.byIds(productIds),
    queryFn: () => productsApi.findByIds(productIds),
    enabled: productIds.length > 0,
    staleTime: 60_000,
  });
  const productById = useMemo(() => {
    const m = new Map<string, Product>();
    for (const p of productsQuery.data?.data ?? []) m.set(p.id, p);
    return m;
  }, [productsQuery.data]);
  // Over/under measures against a flat 8h/day standard, independent of the
  // per-user hoursPerMonth capacity (which still drives the page header chip).
  const expectedPerDay = STANDARD_HOURS_PER_DAY;
  const dayTotal = sumHours(dayEntries.map((e) => e.hours ?? 0));

  // Centralized optimistic mutations — see timesheet-mutations.ts. The
  // day view is a draft-entry surface only; submission lives on the
  // week view (one transaction for the whole week), so we don't pull
  // submitMutation here.
  // The entry's role is the picked activity's own role (resolved server-side),
  // so there's no disambiguation step.
  const {
    createMutation,
    deleteMutation,
    recallEntryMutation,
    createEntry,
    patchEntry,
    deleteEntry,
    recallEntry,
    promptHost,
  } = useTimesheetMutations(user, weekStart);

  // Bumping `draftRowKey` forces React to remount the blank DraftCard at
  // the bottom — used as a "spawn next" signal after a successful save.
  // Bumped directly from the `onCreate` call site below (not derived from
  // `dayEntries.length`): a count-derived key can revisit a value it already
  // used across a create → delete → create sequence (e.g. 1 → 2 → 1 → 2),
  // which stops React from remounting and leaves the just-submitted values
  // sitting in the "new" row instead of a blank one.
  const [draftGeneration, setDraftGeneration] = useState(0);
  const draftRowKey = `draft-${day}-${draftGeneration}`;

  // ⌘/Ctrl+Enter handler (driven by the page route). Focuses the blank draft
  // card's first control so the user can start a new entry from the keyboard;
  // the card is only rendered when the day is unlocked and on-team, so the ref
  // is null otherwise and the handle no-ops.
  const newEntryRef = useRef<HTMLDivElement>(null);
  // The scrollable cards column — `scrollEntryIntoView` queries within it for
  // the freshly-committed card (each card is tagged with `data-entry-id`).
  const listRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(
    ref,
    () => ({
      focusNewEntry: () => {
        const el = newEntryRef.current;
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        el.querySelector<HTMLElement>('button, [role="combobox"], input, [tabindex]')?.focus();
      },
      scrollEntryIntoView: (entryId) => {
        const el = listRef.current?.querySelector<HTMLElement>(
          `[data-entry-id="${CSS.escape(entryId)}"]`,
        );
        if (!el) return false;
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return true;
      },
    }),
    [],
  );

  const submittedCount = dayEntries.filter(
    (e) => e.status === 'submitted' || e.status === 'approved',
  ).length;
  const draftCount = dayEntries.filter((e) => e.status === 'draft').length;
  const rejectedCount = dayEntries.filter((e) => e.status === 'rejected').length;
  const failedCount = dayEntries.filter((e) => e.status === 'failed').length;
  // Day-level badge precedence: failed > rejected > partial > submitted > draft.
  // failed beats rejected because it's the operator-attention case (admin
  // needs to fix a policy/team config) and shouldn't be hidden behind a
  // simpler "rejected" pill.
  const dayStatus: 'draft' | 'partial' | 'submitted' | 'rejected' | 'failed' | null =
    dayEntries.length === 0
      ? null
      : failedCount > 0
        ? 'failed'
        : rejectedCount > 0
          ? 'rejected'
          : submittedCount > 0 && draftCount > 0
            ? 'partial'
            : submittedCount === dayEntries.length
              ? 'submitted'
              : 'draft';

  return (
    // `flex-1 min-h-0` lets this view claim the remaining viewport height so the
    // day strip + section header stay frozen while only the entry cards scroll.
    // No own padding — the enclosing PageShell supplies `p-6`, so this view
    // lines up edge-to-edge with the page header and toolbar above it.
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <DayStrip dates={dates} entries={entries} selectedDay={day} onPickDay={onPickDay} />

      {/* Everything below the strip — the day header and its entry cards —
          sits inside one card, matching every other detail panel in the app,
          instead of floating loose on the page background. */}
      <div className="surface-card flex min-h-0 flex-1 flex-col gap-3 rounded-xl border p-4">
        <DaySectionHeader
          day={day}
          dayTotal={dayTotal}
          expectedPerDay={expectedPerDay}
          dayStatus={dayStatus}
        />

        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          // Only the cards scroll; the strip + section header above stay pinned.
          // `pr-1` keeps the scrollbar from crowding the card edges.
          <div ref={listRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
            {dayEntries.map((entry) => (
              // `data-entry-id` lets the page route's commit-focus effect scroll
              // a timer-produced card into view (see `scrollEntryIntoView`).
              <div key={entry.id} data-entry-id={entry.id}>
                <EntryCard
                  entry={entry}
                  onPatch={(patch) => patchEntry(entry.id, patch)}
                  onDelete={() => deleteEntry(entry.id)}
                  deleting={deleteMutation.isPending}
                  onRecall={() => recallEntry(entry.id)}
                  recalling={recallEntryMutation.isPending}
                  currentActivity={
                    entry.productId ? (productById.get(entry.productId) ?? null) : null
                  }
                  seedDescription={
                    entry.productId ? (productById.get(entry.productId)?.description ?? null) : null
                  }
                  teamCustomers={teamCustomers}
                  selectableCustomers={selectableCustomers}
                  inScopeByCustomer={inScopeByCustomer}
                  inScopeLoaded={inScopeLoaded}
                  recentByCustomer={recentByCustomer}
                  day={day}
                  roleIdsForEntry={roleIdsForEntry}
                  assigneeRoleId={user.serviceRoleId}
                  isDateLocked={isDateLocked}
                  otherHoursOnDay={dayTotal - Number(entry.hours ?? 0)}
                  engagementVersions={engagementVersions}
                  engagementsLoaded={engagementsLoaded}
                />
              </div>
            ))}
            {periodLocked ? (
              <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
                This period is locked.
              </div>
            ) : dayHasAnyTeam ? (
              // `key` lives on the wrapper so the "spawn next" remount still
              // resets the DraftCard inside; `newEntryRef` lets the ⌘/Ctrl+Enter
              // shortcut focus this card's first field.
              <div key={draftRowKey} ref={newEntryRef}>
                <EntryCard
                  entry={null}
                  daySiblings={dayEntries}
                  onCreate={(input) => {
                    setDraftGeneration((g) => g + 1);
                    createEntry({
                      userId: user.id,
                      date: day,
                      ...input,
                    });
                  }}
                  creating={createMutation.isPending}
                  teamCustomers={teamCustomers}
                  selectableCustomers={selectableCustomers}
                  inScopeByCustomer={inScopeByCustomer}
                  inScopeLoaded={inScopeLoaded}
                  recentByCustomer={recentByCustomer}
                  day={day}
                  roleIdsForEntry={roleIdsForEntry}
                  assigneeRoleId={user.serviceRoleId}
                  isDateLocked={isDateLocked}
                  otherHoursOnDay={dayTotal}
                  engagementVersions={engagementVersions}
                  engagementsLoaded={engagementsLoaded}
                />
              </div>
            ) : (
              <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
                Not on any customer's team on this day. Pick a different day or contact your team
                lead.
              </div>
            )}
          </div>
        )}
      </div>
      {promptHost}
    </div>
  );
});

// ───────────────────────────────────────────────────────────────────────────
// Day strip + section header
// ───────────────────────────────────────────────────────────────────────────

function DayStrip({
  dates,
  entries,
  selectedDay,
  onPickDay,
}: {
  dates: string[];
  entries: Timesheet[];
  selectedDay: string;
  onPickDay: (date: string) => void;
}) {
  return (
    // `bg-surface-subtle` on the track (the same cream PillTabs' list and
    // every table header use) with the selected day lifted to `bg-card`
    // (white) — the picked day reads as raised above the rest of the strip
    // instead of the strip reading as flat white with one cream cell in it.
    <div className="flex items-stretch overflow-x-auto rounded-md border bg-surface-subtle">
      {dates.map((d) => {
        const total = sumHours(entries.filter((e) => e.date === d).map((e) => e.hours ?? 0));
        const isSelected = d === selectedDay;
        return (
          <button
            type="button"
            key={d}
            onClick={() => onPickDay(d)}
            className={cn(
              'flex flex-1 flex-col gap-1 px-4 py-3 text-left transition-colors',
              isSelected ? 'bg-card shadow-sm' : 'hover:bg-card/60',
            )}
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {shortDayName(d)}
              </span>
              <span className="text-xs text-muted-foreground">{d.slice(8)}</span>
            </div>
            <div className={cn('text-sm', total > 0 ? 'font-semibold' : 'text-muted-foreground')}>
              {total > 0 ? formatHours(total) : '—'}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function DaySectionHeader({
  day,
  dayTotal,
  expectedPerDay,
  dayStatus,
}: {
  day: string;
  dayTotal: number;
  expectedPerDay: number;
  dayStatus: 'draft' | 'partial' | 'submitted' | 'rejected' | 'failed' | null;
}) {
  const delta = dayTotal - expectedPerDay;
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold">
          {longDayName(day)}, {shortDate(day)}
        </h2>
        <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <span className="tabular-nums">
            {dayTotal.toFixed(1)}h of {expectedPerDay}h
          </span>
          <span
            className={cn(
              'tabular-nums',
              delta === 0
                ? 'text-muted-foreground'
                : delta > 0
                  ? 'text-emerald-700 dark:text-emerald-300'
                  : 'text-amber-700 dark:text-amber-300',
            )}
          >
            · {formatDelta(delta)}
          </span>
          {dayStatus ? <DayStatusBadge status={dayStatus} /> : null}
        </p>
      </div>
    </div>
  );
}

function DayStatusBadge({
  status,
}: {
  status: 'draft' | 'partial' | 'submitted' | 'rejected' | 'failed';
}) {
  const tone =
    status === 'submitted'
      ? 'bg-primary/10 text-primary'
      : status === 'rejected'
        ? 'bg-destructive/10 text-destructive'
        : status === 'failed'
          ? 'bg-yellow-100 text-yellow-900 dark:bg-yellow-900/30 dark:text-yellow-200'
          : status === 'partial'
            ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200'
            : 'bg-muted text-muted-foreground';
  const label =
    status === 'partial'
      ? 'Partial'
      : status === 'failed'
        ? 'Needs review'
        : status[0]!.toUpperCase() + status.slice(1);
  return (
    <span
      className={cn(
        'rounded-sm px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
        tone,
      )}
    >
      {label}
    </span>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// EntryCard — handles both existing entries and the bottom blank "draft"
// card. When `entry` is null, the card is in create-on-touch mode: the
// first time any field gets a value, onCreate fires and the parent will
// re-mount this card with a new key (a fresh blank one will appear below).
// ───────────────────────────────────────────────────────────────────────────

interface CommonProps {
  deleting?: boolean;
  /** Team customers (ALL statuses) — resolves this card's bound customer
   *  label + isInternal, including a deactivated one. */
  teamCustomers: readonly Customer[];
  /** Active-only subset offered in the card's customer dropdown. */
  selectableCustomers: readonly Customer[];
  /** Product IDs in scope per customer (from covering SOWs). Drives the
   *  sectioned product dropdown and the per-card out-of-scope badge.
   *  `inScopeLoaded === false` means the lookup is still in flight — the
   *  card passes `undefined` to the picker in that case so the dropdown
   *  shows a flat list instead of mis-sectioning everything as
   *  out-of-scope. */
  inScopeByCustomer: ReadonlyMap<string, ReadonlySet<string>>;
  inScopeLoaded: boolean;
  /** Recently-used product ids per customer, ordered most-recent-first (from
   *  `useRecentProductsByCustomer`). Feeds the picker's "Recent" band; a
   *  missing key means the assignee has no recents for that customer. */
  recentByCustomer: ReadonlyMap<string, readonly string[]>;
  /** The displayed day. Used by the draft card to look up the membership
   *  for the (selected customer, day) pair when the user hasn't yet
   *  committed an entry. */
  day: string;
  /** Resolves the per-customer service-role ids that gate the product
   *  picker for an entry on (customerId, date). When no membership
   *  covers, falls back to `fallbackRoleId` (caller passes the entry's
   *  pinned `roleId` for existing entries, or the assignee's global
   *  role for drafts) so the picker still scopes meaningfully — an
   *  empty array would surface the entire tenant catalog.
   *  Server enforces the same per-customer rule on write. */
  roleIdsForEntry: (
    customerId: string | null,
    date: string,
    fallbackRoleId: string,
  ) => readonly string[];
  /** Assignee's global service-role id — used as the picker fallback for
   *  draft cards (no entry yet → no historical pinned role to fall back
   *  to). Doesn't gate the server side. */
  assigneeRoleId: string;
  /** True when this entry's period has closed FOR THIS ENTRY — forces the
   *  editor read-only on top of the status-based rule. Called with the
   *  entry's own stage, so a `rejected`/`failed` row stays editable through
   *  settlement while a `draft` beside it does not. */
  isDateLocked: (date: string, customerId: string | null, stage?: TimesheetLockStage) => boolean;
  /** This entry's CURRENT activity's own description — the "seed" the
   *  description follows. Lets the existing-card distinguish an untouched,
   *  activity-derived description (safe to replace when the activity changes)
   *  from user-authored text (preserved). Only meaningful for existing cards;
   *  the draft card tracks user edits with a local dirty flag instead. */
  seedDescription?: string | null;
  /** Sum of the day's OTHER entries (excluding this card's), for the inline
   *  24h/day cap. Parent passes `dayTotal − this entry's hours` for an
   *  existing card, or the full `dayTotal` for the blank draft card. */
  otherHoursOnDay: number;
  /** Engagement coverage for the week, and whether it has settled. Each card
   *  resolves its own (customer, date, activity) from it — the day view has no
   *  rows, so a card IS the unit that binds. */
  engagementVersions: readonly EngagementVersion[];
  engagementsLoaded: boolean;
}
type ExistingProps = CommonProps & {
  entry: Timesheet;
  onPatch: (patch: PatchTimesheetInput) => void;
  onDelete: () => void;
  /** Recall this entry back to draft (submitted entries only). */
  onRecall: () => void;
  /** Pending state for the recall mutation. */
  recalling?: boolean;
  /** This entry's CURRENT activity (resolved from the product cache), or null
   *  when there's no activity yet or its detail is still loading. Drives the
   *  customer-change rule: an activity the NEW customer can't log is cleared
   *  along with its hours. Loading (null) errs on KEEP. */
  currentActivity?: Product | null;
};
type DraftProps = CommonProps & {
  entry: null;
  /** The day's existing entries — the sticky-billable source. A new draft
   *  inherits `billable` from the most-recent entry sharing its (customer,
   *  product), matching the week grid's per-cell inheritance. */
  daySiblings: readonly Timesheet[];
  onCreate: (input: {
    customerId: string | null;
    productId: string | null;
    /** Resolved before the entry exists, so it is born attributed rather than
     *  patched a moment later. */
    engagementId: string | null;
    hours: number | null;
    description: string | null;
    billable: boolean;
  }) => void;
  creating?: boolean;
};

function EntryCard(props: ExistingProps | DraftProps) {
  if (props.entry === null) return <DraftCard {...props} />;
  return <ExistingCard {...props} />;
}

function ExistingCard({
  entry,
  onPatch,
  onDelete,
  deleting,
  teamCustomers,
  selectableCustomers,
  inScopeByCustomer,
  inScopeLoaded,
  recentByCustomer,
  roleIdsForEntry,
  assigneeRoleId,
  isDateLocked,
  seedDescription,
  currentActivity,
  otherHoursOnDay,
  engagementVersions,
  engagementsLoaded,
  onRecall,
  recalling,
}: ExistingProps) {
  // Which engagement this entry's time belongs to, and whether the user is
  // being asked. A card is ONE entry on ONE date, so — unlike the week grid,
  // where a row spans seven days — the row helpers are called with this
  // entry's single date.
  const [engagementOpen, setEngagementOpen] = useState(false);
  const [engagementPrompt, setEngagementPrompt] = useState(false);
  // The activity as PICKED, held locally because the prompt opens in the same
  // breath as the pick. `currentActivity` resolves through the parent's
  // batched `findByIds`, which has not seen a just-chosen product yet, so the
  // dialog's only statement of subject would be the customer alone until that
  // round trip lands. Mirrors the week grid's `pickedProductsRef`.
  const [pickedActivity, setPickedActivity] = useState<Product | null>(null);
  // The activity change the open prompt is about, held UNSAVED: several
  // engagements cover the new activity and the server refuses to save the
  // entry until one is chosen, so the change is saved with the answer —
  // and Cancel just drops it (the picker is controlled by `entry.productId`,
  // which never moved).
  const [pendingActivityPatch, setPendingActivityPatch] = useState<PatchTimesheetInput | null>(
    null,
  );
  // A customer pick that would clear the row's activity AND its logged hours
  // (the new customer can't perform the current activity), pending the
  // user's OK. Canceling needs no separate revert: the picker is controlled
  // by `entry.customerId`, which hasn't changed, so it already shows the
  // original customer underneath the dialog.
  const [confirmCustomerChange, setConfirmCustomerChange] = useState<{
    id: string | null;
    isInternal: boolean;
    newCustomerName: string;
  } | null>(null);
  const engagementRow = {
    customerId: entry.customerId,
    productId: entry.productId,
    engagementId: entry.engagementId,
    dates: [entry.date],
  };
  const engagementResolution: EngagementResolution = engagementsLoaded
    ? resolveRowEngagement(engagementVersions, engagementRow, entry.date)
    : { kind: 'pending' };
  const engagementCandidates = engagementsLoaded
    ? engagementCandidatesForRow(engagementVersions, engagementRow, entry.date)
    : [];
  const engagementsActive = engagementsLoaded
    ? engagementsActiveForRow(engagementVersions, engagementRow, entry.date)
    : [];
  // The DIALOG is about the activity the user just picked, which is not
  // necessarily the one on the entry: the `productId` patch is optimistic, and
  // it can roll back (a failure, or the deferred ROLE_REQUIRED retry). Its
  // title already reads `pickedActivity`, so its options have to resolve from
  // the same activity — otherwise the prompt names one activity and offers
  // another's engagements, and the write guard, which checks only customer and
  // date, accepts the result. The CELL keeps resolving from `entry.productId`,
  // because it reports what the entry actually is.
  const promptCandidates = engagementsLoaded
    ? engagementCandidatesForRow(
        engagementVersions,
        {
          customerId: entry.customerId,
          productId: pickedActivity?.id ?? entry.productId,
          dates: [entry.date],
        },
        entry.date,
      )
    : [];
  // `undefined` while loading so the picker doesn't section everything as
  // out-of-scope before the response lands.
  const inScopeSet =
    inScopeLoaded && entry.customerId ? inScopeByCustomer.get(entry.customerId) : undefined;
  const recentSet = entry.customerId ? recentByCustomer.get(entry.customerId) : undefined;
  // Selected customer drives the product picker's internal scope: an internal
  // customer offers only internal products; any other customer excludes them.
  const selectedCustomer = entry.customerId
    ? (teamCustomers.find((c) => c.id === entry.customerId) ?? null)
    : null;
  // Internal customers can't be invoiced, so their time is never billable.
  // Force the switch off and read-only; the customer-change handler patches
  // any stale billable=true to false when switching to an internal customer.
  const customerIsInternal = selectedCustomer?.isInternal === true;
  const productOutOfScope =
    entry.productId !== null && inScopeSet !== undefined && !inScopeSet.has(entry.productId);
  // Editable mirrors the backend's EDITABLE_TIMESHEET_STATUSES — draft,
  // rejected, and failed are user-editable; submitted/approved are locked.
  // A locked period overrides the status rule: even a draft becomes
  // read-only once its date falls in a locked period.
  // The entry's OWN stage, not the bare submission boundary: a `rejected` /
  // `failed` row is a correction the settlement window exists to allow, and
  // asking on the submission boundary would render it read-only while the API
  // still accepts the PATCH.
  const periodLocked = isDateLocked(
    entry.date,
    entry.customerId,
    timesheetLockStageFor('edited', entry.status),
  );
  const editable = !periodLocked && isTimesheetEditable(entry.status);
  const [hours, setHours] = useState(entry.hours ?? '');
  const [description, setDescription] = useState(entry.description ?? '');
  // Inline hours error (malformed / out-of-range, or over the 24h/day cap) set
  // on blur, or null. Keeps the typed text and shows the message instead of
  // silently reverting to the saved value.
  const [hoursError, setHoursError] = useState<string | null>(null);
  // Focus target when an hours edit is held back for a missing description.
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  // Track focused inputs so a server-driven `entry.updatedAt` change
  // (which fires on every successful patch) doesn't wipe the user's
  // mid-stream typing. Only resync when no input on this card is
  // currently focused.
  const focusCountRef = useRef(0);
  const incrementFocus = () => {
    focusCountRef.current += 1;
  };
  const decrementFocusThen = (commit: () => void) => () => {
    focusCountRef.current = Math.max(0, focusCountRef.current - 1);
    commit();
  };

  useEffect(() => {
    if (focusCountRef.current === 0) {
      setHours(entry.hours ?? '');
      setDescription(entry.description ?? '');
    }
  }, [entry.id, entry.updatedAt, entry.hours, entry.description]);

  const commitHours = () => {
    const trimmed = hours.toString().trim();
    const error = hoursValidationError(trimmed, otherHoursOnDay);
    if (error) {
      // Bad input — keep what the user typed and flag it inline instead of
      // silently reverting to the saved value.
      setHoursError(error);
      return;
    }
    setHoursError(null);
    if (trimmed === '') {
      // User cleared the field. Patch hours → null (allowed for drafts).
      if (entry.hours != null) onPatch({ hours: null });
      return;
    }
    const n = Number(trimmed);
    if (n === Number(entry.hours ?? 0)) return;
    // Logged time requires a description (server enforces at write now). If the
    // row has none yet, hold the hours write and steer to the notes field
    // instead of firing a doomed patch — commitDescription folds the held
    // hours back in once notes are added.
    if (description.trim() === '') {
      descriptionRef.current?.focus();
      return;
    }
    // Fold in the description when it isn't saved yet so the row never lands in
    // the forbidden hours-without-description state in a single patch.
    const patch: PatchTimesheetInput = { hours: n };
    if (description.trim() !== (entry.description ?? '')) patch.description = description.trim();
    onPatch(patch);
  };

  const commitDescription = () => {
    const next = description.trim();
    if (next === (entry.description ?? '')) return;
    // Can't clear the description while time is logged — revert to the saved
    // value (the server rejects an empty description on a row with hours).
    if (next === '' && entry.hours != null) {
      setDescription(entry.description ?? '');
      return;
    }
    // Fold in hours typed while the description was still missing so the held
    // hours write lands together with the notes that unblock it.
    const patch: PatchTimesheetInput = { description: next || null };
    const h = hours.toString().trim();
    // Only fold in held hours that are themselves valid (within range and the
    // day cap) — never let the description save smuggle past hours validation.
    if (h !== '' && hoursValidationError(h, otherHoursOnDay) === null) {
      const n = Number(h);
      if (n !== Number(entry.hours ?? 0)) patch.hours = n;
    }
    onPatch(patch);
  };

  const customerMissing = entry.customerId == null;
  const hoursMissing = entry.hours == null;
  // A logged entry (hours present) needs a description — the same rule the
  // server enforces at submit, surfaced inline as a required-field border so
  // the gap is visible while editing rather than only on the submit attempt.
  const descriptionMissing = entry.hours != null && !entry.description?.trim();
  // Mirrors the week grid's row-level "Pick an activity" nudge (submit
  // rejects hours logged without an activity) — surfaced here too so the day
  // view flags it inline instead of only failing on submit.
  const activityMissing = entry.hours != null && entry.productId == null;

  return (
    <div className="rounded-md border bg-card p-3">
      <div className={CARD_GRID}>
        <div className="flex min-w-0 flex-col">
          <CustomerAutocomplete
            value={entry.customerId}
            disabled={!editable}
            placeholder="Pick customer…"
            className={customerMissing ? 'border-destructive/50 border-dashed' : undefined}
            onChange={(c) => {
              const next = c?.id ?? null;
              if (next === entry.customerId) return;
              // If the row already carries an activity the NEW customer can't log
              // (custom activity pinned elsewhere, internal mismatch, or a role
              // the assignee doesn't hold there), drop the activity AND its hours
              // so the row doesn't carry an invalid pairing. Otherwise keep them.
              // Unknown activity detail (still loading) errs on KEEP.
              const clearActivity =
                next != null &&
                currentActivity != null &&
                !isActivityVisibleForCustomer(currentActivity, {
                  customerId: next,
                  isInternal: c?.isInternal === true,
                  coveringRoleIds: roleIdsForEntry(
                    next,
                    entry.date,
                    entry.customerTeamServiceRoleId ?? assigneeRoleId,
                  ),
                });
              // Only ask when there's actually something to lose — logged
              // hours or notes. A blanked row (no hours/description yet)
              // clears its stale activity straight away.
              const willLoseData =
                clearActivity && (entry.hours != null || !!entry.description?.trim());
              if (willLoseData) {
                setConfirmCustomerChange({
                  id: next,
                  isInternal: c?.isInternal === true,
                  newCustomerName: c?.name ?? 'this customer',
                });
                return;
              }
              if (clearActivity) {
                onPatch({
                  customerId: next,
                  productId: null,
                  hours: null,
                  description: null,
                  ...(c?.isInternal === true ? { billable: false } : {}),
                });
                toast.info('Activity isn’t available for this customer — it was cleared.');
                return;
              }
              // Switching to an internal customer forces billable off in the
              // same patch so a previously-billable entry can't stay billable.
              onPatch(
                c?.isInternal === true && entry.billable
                  ? { customerId: next, billable: false }
                  : { customerId: next },
              );
            }}
            items={selectableCustomers}
            selectedItem={selectedCustomer}
          />
          <ConfirmDialog
            open={confirmCustomerChange !== null}
            title="Change customer?"
            description={
              <>
                {currentActivity?.name ? `“${currentActivity.name}” isn’t` : 'This activity isn’t'}{' '}
                available for {confirmCustomerChange?.newCustomerName ?? 'this customer'}.
                Continuing will clear the activity and delete this row’s logged hours and notes.
                This can’t be undone.
              </>
            }
            confirmLabel="Change customer"
            cancelLabel="Keep current customer"
            destructive
            onConfirm={() => {
              if (confirmCustomerChange) {
                onPatch({
                  customerId: confirmCustomerChange.id,
                  productId: null,
                  hours: null,
                  description: null,
                  ...(confirmCustomerChange.isInternal ? { billable: false } : {}),
                });
                toast.info('Activity isn’t available for this customer — it was cleared.');
              }
              setConfirmCustomerChange(null);
            }}
            onCancel={() => setConfirmCustomerChange(null)}
          />
          {/* Under the customer, as in the week grid — the engagement is an
            attribute of the customer binding, not a fourth column in an
            already-dense card. */}
          <TimesheetEngagementCell
            resolution={engagementResolution}
            candidates={engagementCandidates}
            activeEngagements={engagementsActive}
            readOnly={!editable}
            open={engagementOpen}
            onOpenChange={setEngagementOpen}
            onChange={(engagementId) => onPatch({ engagementId })}
          />
          <TimesheetEngagementDialog
            open={engagementPrompt}
            customerName={selectedCustomer?.name}
            activityName={pickedActivity?.name ?? currentActivity?.name}
            candidates={promptCandidates}
            others={engagementsActive.filter(
              (e) => !promptCandidates.some((c) => c.engagementId === e.engagementId),
            )}
            currentEngagementId={entry.engagementId}
            onSelect={(engagementId) => {
              if (typeof pendingActivityPatch?.description === 'string') {
                setDescription(pendingActivityPatch.description);
              }
              onPatch({ ...pendingActivityPatch, engagementId });
              setPendingActivityPatch(null);
              setEngagementPrompt(false);
            }}
            onDismiss={() => {
              setPendingActivityPatch(null);
              setPickedActivity(null);
              setEngagementPrompt(false);
            }}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <ProductAutocomplete
            value={entry.productId ?? null}
            // Internal customer ⇒ internal then service-role scoping:
            // ProductAutocomplete keeps the role filter and only drops SOW
            // sectioning, so we pass the normal props unconditionally and just
            // flag internal. Server matches on write.
            filterByInternal={selectedCustomer?.isInternal}
            // Disabled until customer is picked — product scope depends on
            // the row's customer, and the backend rejects a product without
            // a covering customer/SOW anyway.
            disabled={!editable || entry.customerId == null}
            placeholder={entry.customerId == null ? 'Pick customer first' : 'Pick an activity'}
            className={activityMissing ? 'border-destructive/50' : undefined}
            // Surface this customer's custom activities alongside the global
            // catalog (and exclude other customers'). Server matches on write.
            customerId={entry.customerId}
            // Scope data is withheld while the out-of-scope UI is hidden, so
            // the dropdown drops its "In/Out of scope" sections, amber dots,
            // and trigger badge. Tied to the same flag as the standalone badge
            // so both restore together.
            inScopeProductIds={SHOW_OUT_OF_SCOPE_BADGE ? inScopeSet : undefined}
            // Catalog scoped to the assignee's PER-CUSTOMER role on this
            // entry's date (from customer_team_members.serviceRoleId).
            // Fallback when no membership covers the date: the entry's recorded
            // customer-team role (the member's role at create time). For a
            // grandfathered entry the picker keeps a meaningful scope instead
            // of surfacing the whole catalog. Server enforces the same
            // per-customer rule on write.
            filterServiceRoleIds={roleIdsForEntry(
              entry.customerId,
              entry.date,
              entry.customerTeamServiceRoleId ?? assigneeRoleId,
            )}
            recentProductIds={recentSet}
            onChange={(p) => {
              const next = p?.id ?? null;
              if (next === (entry.productId ?? null)) return;
              setPickedActivity(p ?? null);
              // Whatever engagement this entry carried was resolved against
              // the OLD activity, so it cannot answer for the new one — clear
              // it in the same patch and let it re-resolve. Same rule the week
              // grid applies on a row's activity change.
              const nextResolution = engagementsLoaded
                ? resolveRowEngagement(
                    engagementVersions,
                    {
                      customerId: entry.customerId,
                      productId: next,
                      engagementId: null,
                      dates: [entry.date],
                    },
                    entry.date,
                  )
                : ({ kind: 'pending' } as EngagementResolution);
              // Let the description follow the activity when the current text is
              // still the prior activity's own (or blank) — never when the user
              // authored something. Fold the new description into the SAME patch
              // as productId so role + notes land atomically.
              const nextDescription = descriptionForActivityChange(
                p?.description,
                isDescriptionPristine(description, seedDescription),
              );
              const activityPatch: PatchTimesheetInput =
                nextDescription !== undefined
                  ? { productId: next, description: nextDescription }
                  : { productId: next };
              // Only `ask` is a question. Zero or one match resolves silently
              // (the server binds the one), so prompting there would demand an
              // answer the user doesn't owe. An ambiguous change is held until
              // answered — see `pendingActivityPatch`.
              if (nextResolution.kind === 'ask') {
                setPendingActivityPatch(activityPatch);
                setEngagementPrompt(true);
                return;
              }
              if (nextDescription !== undefined) setDescription(nextDescription);
              onPatch({ ...activityPatch, engagementId: null });
            }}
          />
          {SHOW_OUT_OF_SCOPE_BADGE && productOutOfScope ? (
            <OutOfScopeBadge className="self-start" />
          ) : null}
        </div>
        <div className="flex flex-col gap-1">
          <Input
            type="text"
            inputMode="decimal"
            aria-label="Hours"
            placeholder="0"
            aria-invalid={hoursError ? true : undefined}
            className={cn(
              'text-right tabular-nums',
              hoursMissing && 'border-dashed border-destructive/50',
            )}
            value={hours}
            disabled={!editable}
            onChange={(e) => {
              // Keep the raw text — don't rewrite what the user typed. Invalid
              // input surfaces as an inline error on blur, not silently
              // stripped. The error clears as soon as they start fixing it.
              setHours(e.target.value);
              if (hoursError) setHoursError(null);
            }}
            onFocus={incrementFocus}
            onBlur={decrementFocusThen(commitHours)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                (e.currentTarget as HTMLInputElement).blur();
              }
            }}
          />
          {hoursError ? (
            <p className="text-right text-[11px] text-destructive">{hoursError}</p>
          ) : null}
        </div>
        <Textarea
          ref={descriptionRef}
          rows={1}
          aria-label="Description"
          placeholder="What did you work on?"
          className={cn(
            'min-h-9 resize-none text-sm',
            descriptionMissing && 'border-dashed border-destructive/50',
          )}
          value={description}
          disabled={!editable}
          onChange={(e) => setDescription(e.target.value)}
          onFocus={incrementFocus}
          onBlur={decrementFocusThen(commitDescription)}
        />
        <div className="flex items-start justify-end">
          {editable ? (
            <>
              {entry.status !== 'draft' ? (
                // Show the badge inline for rejected / failed cards — the
                // user can still edit, but the badge tells them *why* the
                // row landed back here. Reason renders as a tooltip.
                <ApprovalStatusBadge
                  status={entry.status}
                  reason={approvalResolutionText(entry.resolutionReason)}
                  className="mr-1 mt-1.5"
                />
              ) : null}
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Delete entry"
                disabled={deleting}
                onClick={onDelete}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </>
          ) : (
            // Read-only badge for a locked entry. A period lock takes
            // precedence over the status label — an entry whose status is
            // still "draft" but whose date is locked should read "locked,"
            // not "draft," so the user understands why it can't be edited.
            // A submitted/approved entry (and only when NOT period-locked) also
            // gets a per-entry Recall action to pull it back to draft.
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide',
                  // Match the week grid's lock colours: period locked → red,
                  // approved → green, submitted / other → neutral grey.
                  periodLocked
                    ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-200'
                    : entry.status === 'approved'
                      ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-200'
                      : 'bg-muted text-muted-foreground',
                )}
                title={periodLocked ? 'This period is locked.' : undefined}
              >
                {periodLocked ? 'locked' : entry.status}
              </span>
              {entry.status === 'submitted' && !periodLocked ? (
                <RecallEntryButton onConfirm={onRecall} loading={recalling} />
              ) : null}
            </div>
          )}
        </div>
      </div>
      <div className="mt-2">
        <BillableSwitch
          billable={customerIsInternal ? false : entry.billable}
          disabled={!editable || customerIsInternal}
          onToggle={() => onPatch({ billable: !entry.billable })}
        />
      </div>
    </div>
  );
}

/** How long to wait after a field change before auto-saving the draft.
 *  Long enough that picking customer + typing hours fires ONE create with
 *  both fields, short enough that the user can move to the next entry
 *  without explicitly committing. Blurring an input flushes immediately. */
const DRAFT_AUTOSAVE_DEBOUNCE_MS = 700;

function DraftCard({
  daySiblings,
  onCreate,
  creating,
  teamCustomers,
  selectableCustomers,
  inScopeByCustomer,
  inScopeLoaded,
  recentByCustomer,
  day,
  roleIdsForEntry,
  assigneeRoleId,
  otherHoursOnDay,
  engagementVersions,
  engagementsLoaded,
}: DraftProps) {
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  // The engagement the entry will be BORN with. Resolved from local state
  // because the entry doesn't exist yet — an auto-bound one is folded in at
  // create time below, so a draft that resolves to exactly one never needs a
  // follow-up patch.
  const [engagementId, setEngagementId] = useState<string | null>(null);
  const [engagementOpen, setEngagementOpen] = useState(false);
  const [engagementPrompt, setEngagementPrompt] = useState(false);
  // The full picked activity, kept alongside `productId` so a customer change
  // can test whether it's still loggable against the new customer (its gating
  // fields aren't on the id alone). Cleared whenever the activity is cleared.
  const [pickedProduct, setPickedProduct] = useState<Product | null>(null);
  // What the activity pick that opened the engagement prompt replaced, so
  // Cancel can put it back. The card can't be created with an ambiguous
  // activity and no engagement (the server refuses it), so declining the
  // question has to decline the pick that raised it.
  const promptRevertRef = useRef<{
    productId: string | null;
    product: Product | null;
    description: string;
  } | null>(null);
  const [hoursDraft, setHoursDraft] = useState('');
  const [description, setDescription] = useState('');
  // Billable follows the customer until the user overrides it: OFF until a
  // customer is picked, then inherited from a prior same-pair entry (sticky) or
  // defaulted ON for an external customer, and forced OFF for internal.
  // `billableTouched` pins an explicit toggle so a later customer change can't
  // stomp the user's choice. `effectiveBillable` (below) resolves all cases.
  const [billable, setBillable] = useState(false);
  const [billableTouched, setBillableTouched] = useState(false);
  // Tracks whether the user has typed into the description. While false the
  // field is still activity-derived, so picking/changing the activity may
  // pre-fill it from the product (mirrors the week-grid new-entry behavior);
  // once the user edits it, their text is preserved across activity changes.
  const descriptionDirtyRef = useRef(false);
  // Inline hours error set on blur (malformed / out-of-range, or over the
  // 24h/day cap), or null. The draft just won't autosave while invalid; this
  // surfaces *why*.
  const [hoursError, setHoursError] = useState<string | null>(null);

  const inScopeSet = inScopeLoaded && customerId ? inScopeByCustomer.get(customerId) : undefined;
  const recentSet = customerId ? recentByCustomer.get(customerId) : undefined;
  const selectedCustomer = customerId
    ? (teamCustomers.find((c) => c.id === customerId) ?? null)
    : null;
  // Internal customers are never billable — force the switch off + read-only
  // and create the entry as non-billable regardless of the draft state.
  const customerIsInternal = selectedCustomer?.isInternal === true;
  // Prior same-(customer, product) entries on this day — the sticky source,
  // mirroring the week grid's per-cell inheritance.
  const siblingsForPair = daySiblings.filter(
    (e) => e.customerId === customerId && e.productId === productId,
  );
  // Resolved billable for the switch AND the create payload:
  //   - no customer OR internal customer → never billable (switch disabled)
  //   - the user's explicit toggle wins once made (billableTouched)
  //   - else inherit the day's most-recent same-pair entry, else default ON
  //     for the external customer (stickyBillable's fallback).
  const effectiveBillable =
    selectedCustomer == null || customerIsInternal
      ? false
      : billableTouched
        ? billable
        : stickyBillable(siblingsForPair, true);
  const productOutOfScope =
    productId !== null && inScopeSet !== undefined && !inScopeSet.has(productId);
  const engagementRow = { customerId, productId, engagementId, dates: [day] };
  const engagementResolution: EngagementResolution = engagementsLoaded
    ? resolveRowEngagement(engagementVersions, engagementRow, day)
    : { kind: 'pending' };
  const engagementCandidates = engagementsLoaded
    ? engagementCandidatesForRow(engagementVersions, engagementRow, day)
    : [];
  const engagementsActive = engagementsLoaded
    ? engagementsActiveForRow(engagementVersions, engagementRow, day)
    : [];
  // What the create carries: the user's pick when there is one, otherwise
  // whatever the rule bound on its own.
  const effectiveEngagementId =
    engagementId ??
    (engagementResolution.kind === 'bound' ? engagementResolution.engagement.engagementId : null);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasFiredRef = useRef(false);

  // tryCreate captures the latest state via the closure recreated on each
  // render. Returning `false` means "nothing valid to save" so the caller
  // (debounce or blur) can decide whether to clear timers.
  const tryCreate = (): boolean => {
    if (hasFiredRef.current) return false; // a single create per card mount
    // Hold while the engagement dialog is up. Creating here bumps
    // `draftGeneration`, which remounts this card — and the dialog lives
    // inside it, so the prompt would vanish mid-answer and the entry would be
    // created unattributed. The effect below re-arms once the dialog closes,
    // so the create then carries whatever the user chose.
    if (engagementPrompt) return false;
    const trimmed = hoursDraft.trim();
    if (hoursValidationError(trimmed, otherHoursOnDay) !== null) {
      // Invalid / over-cap — don't autosave. The inline error (set on blur by
      // flushSave) tells the user why nothing was saved.
      return false;
    }
    const hoursValue = trimmed === '' ? null : Number(trimmed);
    const touched =
      customerId !== null || productId !== null || trimmed !== '' || description.trim() !== '';
    if (!touched) return false;
    // Logged time requires a description — the server now enforces this at
    // create, not just submit. Hold the autosave until the description is
    // filled rather than firing a doomed request; the dashed border cues it.
    // A draft with no hours yet can still autosave description-less.
    if (hoursValue !== null && description.trim() === '') return false;
    hasFiredRef.current = true;
    onCreate({
      customerId,
      productId,
      engagementId: effectiveEngagementId,
      hours: hoursValue,
      description: description.trim() || null,
      billable: effectiveBillable,
    });
    return true;
  };

  // Hours typed but no description — the create is held until notes land, so
  // surface the same required-field border the existing-row card uses.
  const draftDescriptionMissing = isValidHoursInput(hoursDraft) && description.trim() === '';
  // Hours typed but no activity picked — mirrors the week grid's row-level
  // "Pick an activity" nudge. Doesn't hold the autosave (the week grid lets
  // this save too, only flagging it), just surfaces the gap inline.
  const draftActivityMissing = isValidHoursInput(hoursDraft) && productId === null;

  // Debounced auto-save: every state change schedules a single create
  // attempt DRAFT_AUTOSAVE_DEBOUNCE_MS later. Multiple rapid changes
  // (e.g. pick customer, then type hours within 700ms) collapse into one
  // POST — fixes the previous bug where each field change fired its own
  // create and produced multiple entries per intent.
  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      tryCreate();
      saveTimerRef.current = null;
    }, DRAFT_AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customerId,
    productId,
    hoursDraft,
    description,
    billable,
    billableTouched,
    // Both belong here so the answer actually reaches the create: closing the
    // dialog re-arms the timer, and the fresh `tryCreate` closure reads the
    // engagement that was just picked.
    engagementPrompt,
    engagementId,
  ]);

  // Immediate-save on input blur — flushes any pending debounce.
  const flushSave = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    // On blur, flag an invalid / over-cap value inline. Mid-typing we stay
    // quiet (the debounced autosave just holds off until the value is valid).
    setHoursError(hoursValidationError(hoursDraft, otherHoursOnDay));
    tryCreate();
  };

  return (
    <div className="rounded-md border border-dashed bg-card p-3">
      <div className={CARD_GRID}>
        <div className="flex min-w-0 flex-col">
          <CustomerAutocomplete
            value={customerId}
            placeholder="Add customer…"
            disabled={creating}
            onChange={(c) => {
              const next = c?.id ?? null;
              if (next === customerId) return;
              // Drop a picked activity (and any hours/notes typed for it) when the
              // new customer can't log it — same rule as the saved-row card. The
              // draft hasn't been created yet, so this is a pure local reset.
              const clearActivity =
                next != null &&
                pickedProduct != null &&
                !isActivityVisibleForCustomer(pickedProduct, {
                  customerId: next,
                  isInternal: c?.isInternal === true,
                  coveringRoleIds: roleIdsForEntry(next, day, assigneeRoleId),
                });
              if (clearActivity) {
                setProductId(null);
                setPickedProduct(null);
                setHoursDraft('');
                setDescription('');
                descriptionDirtyRef.current = false;
                toast.info('Activity isn’t available for this customer — it was cleared.');
              }
              setCustomerId(next);
              // An engagement belongs to ONE customer — a move invalidates it.
              setEngagementId(null);
            }}
            items={selectableCustomers}
            selectedItem={selectedCustomer}
          />
          <TimesheetEngagementCell
            resolution={engagementResolution}
            candidates={engagementCandidates}
            activeEngagements={engagementsActive}
            // Frozen while the create is in flight, like the two pickers
            // beside it: `tryCreate` has already fired and posted the
            // engagement it had, so a pick made now is discarded when the
            // response bumps `draftGeneration` and remounts the card.
            readOnly={creating}
            open={engagementOpen}
            onOpenChange={setEngagementOpen}
            onChange={setEngagementId}
          />
          <TimesheetEngagementDialog
            open={engagementPrompt}
            customerName={selectedCustomer?.name}
            activityName={pickedProduct?.name}
            candidates={engagementCandidates}
            others={engagementsActive.filter(
              (e) => !engagementCandidates.some((c) => c.engagementId === e.engagementId),
            )}
            currentEngagementId={engagementId}
            onSelect={(id) => {
              promptRevertRef.current = null;
              setEngagementId(id);
              setEngagementPrompt(false);
            }}
            onDismiss={() => {
              const revert = promptRevertRef.current;
              promptRevertRef.current = null;
              if (revert) {
                setProductId(revert.productId);
                setPickedProduct(revert.product);
                setDescription(revert.description);
              }
              setEngagementPrompt(false);
            }}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <ProductAutocomplete
            value={productId}
            // Internal customer ⇒ ProductAutocomplete keeps role scoping and
            // only drops SOW sectioning; pass the normal props unconditionally
            // and flag internal.
            filterByInternal={selectedCustomer?.isInternal}
            placeholder={customerId == null ? 'Pick customer first' : 'Pick an activity'}
            className={draftActivityMissing ? 'border-destructive/50' : undefined}
            disabled={creating || customerId == null}
            // Surface this customer's custom activities alongside the global
            // catalog (and exclude other customers'). Server matches on write.
            customerId={customerId}
            // Scope data is withheld while the out-of-scope UI is hidden, so
            // the dropdown drops its "In/Out of scope" sections, amber dots,
            // and trigger badge. Tied to the same flag as the standalone badge
            // so both restore together.
            inScopeProductIds={SHOW_OUT_OF_SCOPE_BADGE ? inScopeSet : undefined}
            // New rows scope to the assignee's per-customer role on the
            // selected (customer, day). When no membership covers `day`
            // for `customerId`, fall back to the assignee's global role
            // — keeps the catalog from collapsing to the unfiltered
            // tenant-wide list. Picker is `disabled` until a customer
            // is picked, so the null-customer branch is defensive.
            filterServiceRoleIds={roleIdsForEntry(customerId, day, assigneeRoleId)}
            recentProductIds={recentSet}
            onChange={(p) => {
              const next = p?.id ?? null;
              const replaced = { productId, product: pickedProduct, description };
              setProductId(next);
              setPickedProduct(p ?? null);
              // A pick made for the previous activity can't answer for this
              // one, so drop it and let the rule re-resolve — then ask, but
              // only when the new activity is genuinely ambiguous.
              if (next !== productId) {
                setEngagementId(null);
                const nextResolution = engagementsLoaded
                  ? resolveRowEngagement(
                      engagementVersions,
                      { customerId, productId: next, engagementId: null, dates: [day] },
                      day,
                    )
                  : ({ kind: 'pending' } as EngagementResolution);
                if (nextResolution.kind === 'ask') {
                  promptRevertRef.current = replaced;
                  setEngagementPrompt(true);
                }
              }
              // Pre-fill the description from the picked activity while the
              // field is still untouched — same rule as the existing-card,
              // here keyed off the local dirty flag.
              const nextDescription = descriptionForActivityChange(
                p?.description,
                !descriptionDirtyRef.current,
              );
              if (nextDescription !== undefined) setDescription(nextDescription);
            }}
          />
          {SHOW_OUT_OF_SCOPE_BADGE && productOutOfScope ? (
            <OutOfScopeBadge className="self-start" />
          ) : null}
        </div>
        <div className="flex flex-col gap-1">
          <Input
            type="text"
            inputMode="decimal"
            aria-label="Hours"
            placeholder="0"
            aria-invalid={hoursError ? true : undefined}
            className="text-right tabular-nums"
            value={hoursDraft}
            disabled={creating}
            onChange={(e) => {
              // Keep the raw text — don't rewrite what the user typed. Invalid
              // input surfaces as an inline error on blur, not silently
              // stripped. The error clears as soon as they start fixing it.
              setHoursDraft(e.target.value);
              if (hoursError) setHoursError(null);
            }}
            onBlur={flushSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                (e.currentTarget as HTMLInputElement).blur();
              }
            }}
          />
          {hoursError ? (
            <p className="text-right text-[11px] text-destructive">{hoursError}</p>
          ) : null}
        </div>
        <Textarea
          rows={1}
          aria-label="Description"
          placeholder="What did you work on?"
          className={cn(
            'min-h-9 resize-none text-sm',
            draftDescriptionMissing && 'border-dashed border-destructive/50',
          )}
          value={description}
          disabled={creating}
          onChange={(e) => {
            descriptionDirtyRef.current = true;
            setDescription(e.target.value);
          }}
          onBlur={flushSave}
        />
        <div className="flex items-start justify-end">
          {/* No delete on the blank card — clear the inputs to discard. */}
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground/60">
            {creating ? 'Saving…' : 'New'}
          </span>
        </div>
      </div>
      <div className="mt-2">
        <BillableSwitch
          billable={effectiveBillable}
          disabled={customerIsInternal || selectedCustomer == null}
          onToggle={() => {
            setBillable(!effectiveBillable);
            setBillableTouched(true);
          }}
        />
      </div>
    </div>
  );
}
